import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  claimProject,
  getProjectAccess,
  addProjectMember,
  suggestPeople,
  removeProjectMember,
  createPreviewLink,
  setPreviewLinkAllowStart,
  getPreviewLinkUrl,
  revokePreviewLink,
  setPreviewLinkPassword,
} from '../utils/vfsClient';
import { generateSharePassword, sharePasswordProblem } from '../utils/sharePassword';
import { friction } from '../analytics/journey';
import './ShareDialog.css';
import { markHomeStep } from '../services/SettingsService';
import { getNotificationSettings } from '../services/NotificationsService';
import { listDirectoryEntries } from '../utils/vfsClient';
import { defaultEntry, serviceNames } from '../utils/shareEntry';
import { defaultAppOf, defaultRunnableOf, loadDefaultApp } from '../utils/defaultApp';
import { parseRunnableName } from '../utils/runnablesModel';

// "Share": who can open the project (owner manages people) and public preview links (editors+).
// Contract: flow-helm-chart docs/project-sharing.md ("Roles", "vfs-service REST", "UI").

export const EXPIRY_OPTIONS = [
  { hours: 168, label: 'in 7 days' },
  { hours: 24, label: 'in 24 hours' },
  { hours: 720, label: 'in 30 days' },
];

const PASSWORD_HINT = 'Visitors type it once; it lasts until they close the browser. After 5 wrong tries the link locks for that visitor. '
  + 'Send it separately from the link. Flow keeps only a hash: you can’t see it again later.';

const LockIcon = () => (
  <svg aria-hidden="true" fill="none" focusable="false" height="12" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12">
    <rect height="10" rx="2" width="14" x="5" y="11" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const ROLE_LABELS = { owner: 'Owner', editor: 'Can edit', viewer: 'Can view' };
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SEPARATOR = /[\s,;]+/;

/** The addresses in typed or pasted text (separated by commas, semicolons, spaces or new lines), lower-cased, no repeats. */
const splitEmails = (text) => [...new Set(String(text || '').split(SEPARATOR).map((part) => part.trim().toLowerCase()).filter(Boolean))];
/** An entry's name in the picker: a pinned file the default app is as Apps names it ("app · docker-compose.yaml"). */
const entryLabel = (projectId, name) => {
  const runnable = defaultAppOf(projectId) === name ? defaultRunnableOf(projectId) : null;
  return runnable ? `${runnable.service.replace(/^services\//, '')} · ${runnable.file}` : name;
};

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const formatDate = (iso) => {
  if (!iso) { return '—'; }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) { return '—'; }
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const isExpired = (iso) => {
  const time = new Date(iso).getTime();
  return Number.isFinite(time) && time <= Date.now();
};

/** "Expires in 7 days", "Expires tomorrow", "Expires in 5 h", "Expired". */
const expiresLabel = (iso) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) { return 'Expires —'; }
  if (ms <= 0) { return 'Expired'; }
  const hours = ms / 3_600_000;
  if (hours < 1) { return 'Expires within the hour'; }
  if (hours < 20) { return `Expires in ${Math.round(hours)} h`; }
  const days = Math.round(hours / 24);
  return days <= 1 ? 'Expires tomorrow' : `Expires in ${days} days`;
};

/** Copies `text`; false when the browser refuses (the caller selects the field instead). */
const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

/** What to tell the person when a sharing request fails, by HTTP status. */
const describeFailure = (error, action) => {
  if (error?.status === 403) {
    return `You don't have permission to ${action}.`;
  }
  if (error?.status === 404) {
    return `Couldn't ${action}: it no longer exists. The list has been refreshed.`;
  }
  return `Couldn't ${action}. ${error?.message || 'Please try again.'}`;
};

export default function ShareDialog({ projectId, projectName, serviceName, onClose, onAccessChange, focus = null }) {
  const titleId = useId();
  const emailId = useId();
  const emailErrorId = useId();
  const roleId = useId();
  const expiryId = useId();
  const newLinkId = useId();
  const passwordId = useId();
  const passwordErrorId = useId();
  const passwordHintId = useId();
  const editPasswordId = useId();
  const editErrorId = useId();
  const dialogRef = useRef(null);
  const emailRef = useRef(null);
  const linkInputRef = useRef(null);
  const passwordRef = useRef(null);
  const editPasswordRef = useRef(null);

  const [access, setAccess] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  // Addresses already typed and waiting for Add (several can be added at once), and the suggestions for what is being typed.
  const [pending, setPending] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const suggestId = useId();
  const [role, setRole] = useState('viewer');
  const [emailError, setEmailError] = useState('');
  const [busy, setBusy] = useState(null); // 'add' | `remove:<email>` | 'create' | `revoke:<id>`
  const [peopleError, setPeopleError] = useState('');
  const [linkError, setLinkError] = useState('');
  const [expiry, setExpiry] = useState(168);
  // The entry point of a new link: which service it opens (a choice when the project has several).
  const [entries, setEntries] = useState([]);
  const [entryService, setEntryService] = useState(serviceName || '');
  const entryId = useId();
  const [newLink, setNewLink] = useState(null);
  const [copyState, setCopyState] = useState(''); // '' | 'copied' | 'manual'
  // A new link's password: chosen (or generated) here, sent once, never shown by the server again.
  const [requirePassword, setRequirePassword] = useState(false);
  // Ticked by default: whoever opens the link can start all the services when they are down.
  const [allowStart, setAllowStart] = useState(true);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);
  // "New password" / "Add a password" on a link: { linkId, hadPassword, value, error, saved, copied }.
  const [editing, setEditing] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  // Whether the organization sends sharing emails (an older backend, or any failure: not said).
  const [emailsOn, setEmailsOn] = useState(false);

  useEffect(() => {
    let active = true;
    getNotificationSettings().then((value) => { if (active) { setEmailsOn(Boolean(value?.available)); } }).catch(() => {});
    return () => { active = false; };
  }, []);

  const publish = useCallback((next) => {
    setAccess(next);
    if (onAccessChange) { onAccessChange(next); }
  }, [onAccessChange]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      publish(await getProjectAccess(projectId));
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [projectId, publish]);

  useEffect(() => { load(); }, [load]);
  // The services a new link can open (the folders of services/), for the entry point choice.
  useEffect(() => {
    let cancelled = false;
    // The service in view first, else the project's default app (utils/defaultApp), else `app` or the first.
    Promise.all([listDirectoryEntries(projectId, 'services'), loadDefaultApp(projectId)])
      .then(([{ entries: listed }, defaultApp]) => {
        if (cancelled) { return; }
        // A default app that is a file pinned in a service (utils/defaultApp) runs as a session of its own: a link
        // can open it too (`app--compose-docker-compose-yaml`, the runner's name).
        const folders = serviceNames(listed);
        const names = defaultApp && !folders.includes(defaultApp) && parseRunnableName(defaultApp) ? [...folders, defaultApp] : folders;
        setEntries(names);
        setEntryService((current) => defaultEntry(names, names.includes(current) ? current : defaultApp));
      })
      .catch(() => { /* no list: the link opens the service in view, or app */ });
    return () => { cancelled = true; };
  }, [projectId]);

  // Focus: into the dialog on open, back to whatever opened it on close.
  useEffect(() => {
    const opener = document.activeElement;
    dialogRef.current?.focus();
    return () => { if (opener && typeof opener.focus === 'function') { opener.focus(); } };
  }, []);
  // Opened on its people (`focus` 'people'): once the people are read, their section comes into
  // view and the invite field takes the focus (the owner's; anyone else reads who is in).
  useEffect(() => {
    if (focus !== 'people' || loading) { return; }
    document.getElementById(`${titleId}-people`)?.scrollIntoView?.({ block: 'start' });
    emailRef.current?.focus();
  }, [focus, loading, titleId]);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) { return; }
    const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE));
    if (focusable.length === 0) { return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const callerRole = access?.role || 'none';
  const isOwner = callerRole === 'owner';
  const canManageLinks = isOwner || callerRole === 'editor';
  const members = access?.members || [];
  const links = (access?.previewLinks || []).filter((link) => !link.revoked);

  // The draft's finished addresses become chips; the last one stays in the field while it is still being typed.
  const takeDraft = (text) => {
    const finished = /[\s,;]$/.test(text);
    const parts = splitEmails(text);
    const rest = finished ? '' : (text.split(SEPARATOR).pop() || '');
    const complete = finished ? parts : parts.slice(0, Math.max(parts.length - (rest ? 1 : 0), 0));
    if (complete.length > 0) {
      setPending((current) => [...new Set([...current, ...complete])]);
    }
    return rest;
  };

  const onEmailChange = (event) => {
    const text = event.target.value;
    setEmail(SEPARATOR.test(text) ? takeDraft(text) : text);
    setActiveSuggestion(-1);
    if (emailError) { setEmailError(''); }
  };

  const onEmailPaste = (event) => {
    const text = event.clipboardData?.getData('text') || '';
    if (!SEPARATOR.test(text.trim())) { return; }
    event.preventDefault();
    setEmail(takeDraft(`${email}${text} `));
    if (emailError) { setEmailError(''); }
  };

  const pickSuggestion = (address) => {
    setPending((current) => [...new Set([...current, address])]);
    setEmail('');
    setSuggestions([]);
    setActiveSuggestion(-1);
    emailRef.current?.focus();
  };

  const onEmailKeyDown = (event) => {
    if (suggestions.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveSuggestion((index) => (index + step + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter' && activeSuggestion >= 0 && suggestions[activeSuggestion]) {
      event.preventDefault();
      pickSuggestion(suggestions[activeSuggestion].email);
    } else if (event.key === 'Escape' && suggestions.length > 0) {
      event.stopPropagation();
      setSuggestions([]);
    } else if (event.key === 'Backspace' && !email && pending.length > 0) {
      setPending((current) => current.slice(0, -1));
    }
  };

  // Suggest the people already on Flow for the address being typed (not the owner, not who is already shared with).
  useEffect(() => {
    const query = email.trim();
    if (!isOwner || query.length < 2) { setSuggestions([]); return undefined; }
    let active = true;
    const timer = setTimeout(() => {
      suggestPeople(query).then((found) => {
        if (!active) { return; }
        const taken = new Set([...pending, access?.owner?.email, ...(access?.members || []).map((m) => m.email)].filter(Boolean).map((e) => e.toLowerCase()));
        setSuggestions((Array.isArray(found) ? found : []).filter((person) => person?.email && !taken.has(person.email.toLowerCase())).slice(0, 6));
      }).catch(() => { if (active) { setSuggestions([]); } });
    }, 200);
    return () => { active = false; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, isOwner]);

  const addMember = async (event) => {
    event.preventDefault();
    const typed = splitEmails(email);
    const all = [...new Set([...pending, ...typed])];
    if (all.length === 0 || all.some((value) => !EMAIL_PATTERN.test(value))) {
      const bad = all.filter((value) => !EMAIL_PATTERN.test(value));
      setEmailError(bad.length > 1 ? `These are not valid email addresses: ${bad.join(', ')}.` : 'Enter a valid email address, like name@example.com.');
      emailRef.current?.focus();
      return;
    }
    const ownerEmail = access?.owner?.email?.toLowerCase();
    const wanted = all.filter((value) => value !== ownerEmail);
    if (wanted.length === 0) {
      setEmailError('That is the owner of this project.');
      emailRef.current?.focus();
      return;
    }
    setEmailError('');
    setPeopleError('');
    setSuggestions([]);
    setBusy('add');
    const added = [];
    let latest = access;
    try {
      for (const value of wanted) {
        const updated = await addProjectMember(projectId, value, role);
        added.push(value);
        latest = { ...latest, members: updated };
        publish(latest);
      }
      markHomeStep('shared');
      setEmail('');
      setPending([]);
      const who = added.length === 1 ? added[0] : `${added.length} people`;
      setAnnouncement(`${who} can now ${role === 'editor' ? 'edit' : 'view'} this project.${emailsOn ? ' We\'ve emailed them a heads-up.' : ''}`);
    } catch (error) {
      friction('share', true);
      if (added.length > 0) { markHomeStep('shared'); }
      // The ones that went through are done; the rest stay in the list to try again.
      setPending(wanted.filter((value) => !added.includes(value)));
      setEmail('');
      const failed = wanted[added.length];
      setPeopleError(describeFailure(error, added.length > 0 ? `add ${failed} (${added.length} added before it)` : 'add this person'));
      if (error?.status === 403 || error?.status === 404) { load(); }
    } finally {
      setBusy(null);
    }
  };

  const becomeOwner = async () => {
    setPeopleError('');
    setBusy('claim');
    try {
      const owner = await claimProject(projectId);
      publish({ ...access, role: 'owner', owner });
      setAnnouncement('You are now the owner of this project and can share it.');
    } catch (error) {
      friction('share', true);
      setPeopleError(error?.status === 409 ? 'Someone else became the owner of this project first.' : describeFailure(error, 'become the owner'));
      load();
    } finally {
      setBusy(null);
    }
  };

  const removeMember = async (memberEmail) => {
    setPeopleError('');
    setBusy(`remove:${memberEmail}`);
    try {
      const updated = await removeProjectMember(projectId, memberEmail);
      publish({ ...access, members: updated });
      setAnnouncement(`${memberEmail} no longer has access.`);
    } catch (error) {
      friction('share', true);
      setPeopleError(describeFailure(error, `remove ${memberEmail}`));
      if (error?.status === 403 || error?.status === 404) { load(); }
    } finally {
      setBusy(null);
    }
  };

  const togglePassword = (checked) => {
    setRequirePassword(checked);
    setPasswordError('');
    setPasswordCopied(false);
    if (checked && !password) { setPassword(generateSharePassword()); }
  };

  const copyPassword = async () => {
    if (await copyText(password)) {
      setPasswordCopied(true);
      setAnnouncement('Password copied to the clipboard.');
    } else {
      passwordRef.current?.select();
      setAnnouncement('Copy failed: the password is selected, press Ctrl+C or Cmd+C to copy it.');
    }
  };

  const createLink = async () => {
    setLinkError('');
    setCopyState('');
    if (requirePassword) {
      const problem = sharePasswordProblem(password);
      if (problem) {
        setPasswordError(problem);
        passwordRef.current?.focus();
        return;
      }
    }
    setPasswordError('');
    setBusy('create');
    try {
      const created = await createPreviewLink(projectId, {
        service: entryService || serviceName || undefined, expiresInHours: expiry, allowStart, ...(requirePassword ? { password } : {}),
      });
      markHomeStep('shared');
      const isProtected = Boolean(created.protected ?? requirePassword);
      // The password stays in this dialog only, to be copied once more; the server never shows it again.
      setNewLink({ ...created, protected: isProtected, password: requirePassword ? password : '' });
      const entry = {
        id: created.id, createdAt: new Date().toISOString(), expiresAt: created.expiresAt, revoked: false, service: entryService || serviceName || 'app', protected: isProtected, allowStart: Boolean(created.allowStart ?? allowStart),
      };
      publish({ ...access, previewLinks: [entry, ...(access?.previewLinks || []).filter((link) => link.id !== created.id)] });
      setRequirePassword(false);
      setPassword('');
      setPasswordCopied(false);
      setAnnouncement(isProtected
        ? 'Preview link with a password created. You can copy the link again from the list; the password is shown only now.'
        : 'Preview link created. You can copy it again from the list until it expires.');
      // Hand focus to the new URL so it can be read and copied right away.
      setTimeout(() => linkInputRef.current?.focus(), 0);
    } catch (error) {
      friction('share', true);
      setLinkError(describeFailure(error, 'create a preview link'));
      if (error?.status === 403) { load(); }
    } finally {
      setBusy(null);
    }
  };

  const revokeLink = async (linkId) => {
    setLinkError('');
    setBusy(`revoke:${linkId}`);
    try {
      await revokePreviewLink(projectId, linkId);
      publish({ ...access, previewLinks: (access?.previewLinks || []).filter((link) => link.id !== linkId) });
      if (newLink?.id === linkId) { setNewLink(null); }
      if (editing?.linkId === linkId) { setEditing(null); }
      setAnnouncement('Preview link revoked.');
    } catch (error) {
      if (error?.status === 404) {
        // Already gone: drop it from the list rather than report a failure.
        publish({ ...access, previewLinks: (access?.previewLinks || []).filter((link) => link.id !== linkId) });
        setAnnouncement('Preview link revoked.');
      } else {
        friction('share', true);
        setLinkError(describeFailure(error, 'revoke this link'));
        if (error?.status === 403) { load(); }
      }
    } finally {
      setBusy(null);
    }
  };

  // An existing link's URL again (the server signs the same token): copied, or shown selected when the browser refuses.
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [shownLink, setShownLink] = useState(null); // { id, url } when copying failed
  const copyExistingLink = async (linkId) => {
    setLinkError('');
    setBusy(`copy:${linkId}`);
    let url = null;
    try {
      ({ url } = await getPreviewLinkUrl(projectId, linkId));
      await navigator.clipboard.writeText(url);
      setShownLink(null);
      setCopiedLinkId(linkId);
      setAnnouncement('Link copied to the clipboard.');
    } catch (error) {
      if (url) {
        setShownLink({ id: linkId, url });
        setAnnouncement('Copy failed: the link is shown below it, selected; press Ctrl+C or Cmd+C to copy it.');
      } else {
        setLinkError(describeFailure(error, 'get this link'));
      }
    } finally {
      setBusy(null);
    }
  };

  const toggleAllowStart = async (link) => {
    setLinkError('');
    setBusy(`start:${link.id}`);
    try {
      const updated = await setPreviewLinkAllowStart(projectId, link.id, !link.allowStart);
      updateLink(updated || { id: link.id, allowStart: !link.allowStart });
      setAnnouncement(link.allowStart
        ? 'Visitors of this link can no longer start the app.'
        : 'Visitors of this link can start all the services if they are down.');
    } catch (error) {
      friction('share', true);
      setLinkError(describeFailure(error, 'change the link'));
      if (error?.status === 403 || error?.status === 404 || error?.status === 410) { load(); }
    } finally {
      setBusy(null);
    }
  };

  const startPasswordEdit = (link) => {
    setLinkError('');
    // `hadPassword`: the wording stays as it began ("New password" vs "Add a password") once it is saved.
    setEditing({ linkId: link.id, hadPassword: Boolean(link.protected), value: generateSharePassword(), error: '', saved: false, copied: false });
    setTimeout(() => editPasswordRef.current?.focus(), 0);
  };

  const updateLink = (updated) => {
    publish({ ...access, previewLinks: (access?.previewLinks || []).map((link) => (link.id === updated.id ? { ...link, ...updated } : link)) });
  };

  const savePassword = async (remove = false) => {
    if (!editing) { return; }
    const { linkId, value } = editing;
    if (!remove) {
      const problem = sharePasswordProblem(value);
      if (problem) {
        setEditing({ ...editing, error: problem });
        editPasswordRef.current?.focus();
        return;
      }
    }
    setBusy(`password:${linkId}`);
    try {
      const updated = await setPreviewLinkPassword(projectId, linkId, remove ? null : value);
      updateLink(updated || { id: linkId, protected: !remove });
      if (remove) {
        setEditing(null);
        setAnnouncement('Password removed: anyone with the link can open the preview.');
      } else {
        setEditing({ ...editing, error: '', saved: true });
        setAnnouncement('New password saved. Everyone who had opened the link must enter it. Copy it now: it is not shown again.');
      }
    } catch (error) {
      friction('share', true);
      setEditing({ ...editing, error: describeFailure(error, remove ? 'remove the password' : 'set the password') });
      if (error?.status === 403 || error?.status === 404 || error?.status === 410) { load(); }
    } finally {
      setBusy(null);
    }
  };

  const copyEditedPassword = async () => {
    if (!editing) { return; }
    if (await copyText(editing.value)) {
      setEditing({ ...editing, copied: true });
      setAnnouncement('Password copied to the clipboard.');
    } else {
      editPasswordRef.current?.select();
      setAnnouncement('Copy failed: the password is selected, press Ctrl+C or Cmd+C to copy it.');
    }
  };

  const copyNewLinkPassword = async () => {
    if (await copyText(newLink.password)) {
      setNewLink({ ...newLink, passwordCopied: true });
      setAnnouncement('Password copied to the clipboard.');
    } else {
      setAnnouncement('Copy failed: select the password and press Ctrl+C or Cmd+C.');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(newLink.url);
      setCopyState('copied');
      setAnnouncement('Link copied to the clipboard.');
    } catch {
      linkInputRef.current?.select();
      setCopyState('manual');
      setAnnouncement('Copy failed: the link is selected, press Ctrl+C or Cmd+C to copy it.');
    }
  };

  let body;
  if (loading && !access) {
    body = <p className="share-note" role="status">Loading who has access…</p>;
  } else if (loadError || callerRole === 'none') {
    const status = loadError?.status;
    let message = 'You don\'t have access to this project\'s sharing settings.';
    if (status === 404) {
      message = 'Sharing isn\'t available for this project.';
    } else if (loadError && status !== 403) {
      message = `Couldn't load who has access. ${loadError.message || ''}`.trim();
    }
    body = (
      <div className="share-banner share-banner-bad" role="alert">
        <span>{message}</span>
        {loadError && status !== 403 && status !== 404 && <button onClick={load} type="button">Retry</button>}
      </div>
    );
  } else {
    body = (
      <>
        {!isOwner && !access?.owner && callerRole === 'editor' && (
          <div className="share-banner" role="note">
            <span>This project has no owner yet, so it can&apos;t be shared. Become its owner to choose who has access.</span>
            <button className="share-btn share-btn-primary" disabled={busy === 'claim'} onClick={becomeOwner} type="button">
              {busy === 'claim' ? 'Becoming owner…' : 'Become owner'}
            </button>
          </div>
        )}
        {!isOwner && access?.owner && (
          <p className="share-note">
            {callerRole === 'viewer'
              ? 'You can view this project. Only the owner can change who has access.'
              : 'You can edit this project. Only the owner can change who has access.'}
          </p>
        )}

        <section aria-labelledby={`${titleId}-people`} className="share-section">
          <h3 className="share-section-title" id={`${titleId}-people`}>People with access</h3>
          {peopleError && <div className="share-banner share-banner-bad" role="alert"><span>{peopleError}</span></div>}
          <ul className="share-people">
            {access?.owner && (
              <li className="share-person">
                <span className="share-person-who">
                  <span className="share-person-name">{access.owner.name || access.owner.email}</span>
                  {access.owner.name && access.owner.email && <span className="share-person-email">{access.owner.email}</span>}
                </span>
                <span className="share-role">{ROLE_LABELS.owner}</span>
              </li>
            )}
            {members.map((member) => (
              <li className="share-person" key={member.email}>
                <span className="share-person-who">
                  <span className="share-person-name">{member.email}</span>
                </span>
                <span className="share-role">{ROLE_LABELS[member.role] || member.role}</span>
                {isOwner && (
                  <button
                    aria-label={`Remove ${member.email}`}
                    className="share-btn share-btn-quiet"
                    disabled={busy === `remove:${member.email}`}
                    onClick={() => removeMember(member.email)}
                    type="button"
                  >
                    {busy === `remove:${member.email}` ? 'Removing…' : 'Remove'}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {members.length === 0 && <p className="share-note">Not shared with anyone yet.</p>}

          {isOwner && (
            <form className="share-add" noValidate onSubmit={addMember}>
              <div className="share-field share-field-grow share-combo">
                <label htmlFor={emailId}>Email</label>
                <div className="share-chip-box">
                  {pending.map((address) => (
                    <span className="share-chip" key={address}>
                      {address}
                      <button aria-label={`Don't add ${address}`} className="share-chip-x" onClick={() => setPending((current) => current.filter((value) => value !== address))} type="button">×</button>
                    </span>
                  ))}
                  <input
                    aria-activedescendant={activeSuggestion >= 0 ? `${suggestId}-${activeSuggestion}` : undefined}
                    aria-autocomplete="list"
                    aria-controls={suggestions.length > 0 ? suggestId : undefined}
                    aria-describedby={emailError ? emailErrorId : undefined}
                    aria-expanded={suggestions.length > 0}
                    aria-invalid={emailError ? 'true' : 'false'}
                    autoComplete="off"
                    id={emailId}
                    inputMode="email"
                    onBlur={() => setTimeout(() => setSuggestions([]), 150)}
                    onChange={onEmailChange}
                    onKeyDown={onEmailKeyDown}
                    onPaste={onEmailPaste}
                    placeholder={pending.length > 0 ? 'Add another…' : 'name@example.com, or several separated by commas'}
                    ref={emailRef}
                    role="combobox"
                    type="text"
                    value={email}
                  />
                </div>
                {suggestions.length > 0 && (
                  <ul className="share-suggestions" id={suggestId} role="listbox">
                    {suggestions.map((person, index) => (
                      <li
                        aria-selected={index === activeSuggestion}
                        className={index === activeSuggestion ? 'share-suggestion is-active' : 'share-suggestion'}
                        id={`${suggestId}-${index}`}
                        key={person.email}
                        onMouseDown={(event) => { event.preventDefault(); pickSuggestion(person.email); }}
                        role="option"
                      >
                        <span className="share-person-name">{person.name || person.email}</span>
                        {person.name && <span className="share-person-email">{person.email}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="share-field">
                <label htmlFor={roleId}>Role</label>
                <select id={roleId} onChange={(event) => setRole(event.target.value)} value={role}>
                  <option value="viewer">Can view</option>
                  <option value="editor">Can edit</option>
                </select>
              </div>
              <button className="share-btn share-btn-primary" disabled={busy === 'add' || (!email.trim() && pending.length === 0)} type="submit">
                {busy === 'add' ? 'Adding…' : (pending.length + splitEmails(email).length > 1 ? `Add ${pending.length + splitEmails(email).length} people` : 'Add')}
              </button>
              {emailError && <p className="share-field-error" id={emailErrorId}>{emailError}</p>}
            </form>
          )}
          {isOwner && emailsOn && <p className="share-note">People you add get a short email with a link to the project.</p>}
        </section>

        {canManageLinks && (
          <section aria-labelledby={`${titleId}-new-link`} className="share-section">
            <h3 className="share-section-title" id={`${titleId}-new-link`}>New public link</h3>
            <p className="share-note">Anyone with the link can open the running preview, without signing in, until it expires or is revoked.</p>
            {linkError && <div className="share-banner share-banner-bad" role="alert"><span>{linkError}</span></div>}
            <div className="share-add">
              {entries.length > 1 && (
                <div className="share-field">
                  <label htmlFor={entryId}>Opens</label>
                  <select id={entryId} onChange={(event) => setEntryService(event.target.value)} value={entryService}>
                    {entries.map((name) => <option key={name} value={name}>{entryLabel(projectId, name)}</option>)}
                  </select>
                </div>
              )}
              <div className="share-field">
                <label htmlFor={expiryId}>Expires</label>
                <select id={expiryId} onChange={(event) => setExpiry(Number(event.target.value))} value={expiry}>
                  {EXPIRY_OPTIONS.map((option) => <option key={option.hours} value={option.hours}>{option.label}</option>)}
                </select>
              </div>
            </div>

            <div className="share-pw-box">
              <label className="share-check">
                <input checked={requirePassword} onChange={(event) => togglePassword(event.target.checked)} type="checkbox" />
                Require a password
              </label>
              {requirePassword && (
                <>
                  <div className="share-pw-row">
                    <label className="share-sr-only" htmlFor={passwordId}>Password</label>
                    <input
                      aria-describedby={[passwordError ? passwordErrorId : '', passwordHintId].filter(Boolean).join(' ')}
                      aria-invalid={passwordError ? 'true' : 'false'}
                      autoComplete="off"
                      className="share-link-input share-pw-input"
                      id={passwordId}
                      onChange={(event) => { setPassword(event.target.value); setPasswordCopied(false); if (passwordError) { setPasswordError(''); } }}
                      ref={passwordRef}
                      spellCheck={false}
                      type="text"
                      value={password}
                    />
                    <button className="share-btn" onClick={() => { setPassword(generateSharePassword()); setPasswordCopied(false); setPasswordError(''); }} type="button">Generate</button>
                    <button aria-label="Copy password" className="share-btn" onClick={copyPassword} type="button">{passwordCopied ? 'Copied' : 'Copy'}</button>
                  </div>
                  {passwordError && <p className="share-field-error" id={passwordErrorId}>{passwordError}</p>}
                  <p className="share-note" id={passwordHintId}>{PASSWORD_HINT}</p>
                </>
              )}
            </div>

            <div className="share-pw-box">
              <label className="share-check">
                <input checked={allowStart} onChange={(event) => setAllowStart(event.target.checked)} type="checkbox" />
                Anyone with the link can start all the services if they are down
              </label>
              <p className="share-note">Their Start button brings the whole app back; it uses your sandbox hours. Untick it and only you and the people you added can start it.</p>
            </div>

            <div className="share-create-row">
              <button className="share-btn share-btn-primary" disabled={busy === 'create'} onClick={createLink} type="button">
                {busy === 'create' ? 'Creating…' : 'Create link'}
              </button>
            </div>

            {newLink?.url && (
              <div className="share-new-link">
                <label htmlFor={newLinkId}>New link — you can copy it again from the list until it expires</label>
                <div className="share-new-link-row">
                  <input
                    className="share-link-input"
                    id={newLinkId}
                    onFocus={(event) => event.target.select()}
                    readOnly
                    ref={linkInputRef}
                    type="text"
                    value={newLink.url}
                  />
                  <button className="share-btn" onClick={copyLink} type="button">{copyState === 'copied' ? 'Copied' : 'Copy'}</button>
                </div>
                {copyState === 'manual' && <p className="share-field-error">Couldn&apos;t copy automatically: press Ctrl+C (Cmd+C on Mac).</p>}
                {newLink.protected && newLink.password && (
                  <div className="share-new-link-row share-new-link-password">
                    <span className="share-note">Password, shown only now — send it separately:</span>
                    <code className="share-pw-code">{newLink.password}</code>
                    <button aria-label="Copy the new link's password" className="share-btn share-btn-quiet" onClick={copyNewLinkPassword} type="button">
                      {newLink.passwordCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        <section aria-labelledby={`${titleId}-links`} className="share-section">
          <h3 className="share-section-title" id={`${titleId}-links`}>Public links</h3>
          {!canManageLinks && linkError && <div className="share-banner share-banner-bad" role="alert"><span>{linkError}</span></div>}
          {links.length > 0 ? (
            <ul aria-label="Existing preview links" className="share-links">
              {links.map((link) => {
                const expired = isExpired(link.expiresAt);
                const created = formatDate(link.createdAt);
                const isEditing = editing?.linkId === link.id;
                const passwordAction = link.protected ? 'New password' : 'Add a password';
                return (
                  <li className="share-link" key={link.id}>
                    <span className="share-link-main">
                      <span className="share-link-head">
                        <strong>{link.service || serviceName || 'app'}</strong>
                        {link.protected
                          ? <span className="share-pill"><LockIcon />Password</span>
                          : <span className="share-pill">No password</span>}
                        <span className="share-pill" title={formatDate(link.expiresAt)}>{expiresLabel(link.expiresAt)}</span>
                        {link.allowStart && <span className="share-pill" title="Visitors can start all the services when they are down">Can start</span>}
                      </span>
                      <span className="share-link-dates"><span>Created {created}</span></span>
                    </span>
                    {canManageLinks && !expired && (
                      <button
                        aria-label={`Copy link created ${created}`}
                        className="share-btn share-btn-quiet"
                        disabled={busy === `copy:${link.id}`}
                        onClick={() => copyExistingLink(link.id)}
                        type="button"
                      >
                        {copiedLinkId === link.id ? 'Copied' : 'Copy link'}
                      </button>
                    )}
                    {canManageLinks && !expired && (
                      <button
                        aria-expanded={isEditing}
                        aria-label={`${passwordAction} for the link created ${created}`}
                        className="share-btn share-btn-quiet"
                        onClick={() => (isEditing ? setEditing(null) : startPasswordEdit(link))}
                        type="button"
                      >
                        {passwordAction}
                      </button>
                    )}
                    {canManageLinks && !expired && (
                      <button
                        aria-label={`${link.allowStart ? 'Do not let visitors start' : 'Let visitors start'} the app, link created ${created}`}
                        className="share-btn share-btn-quiet"
                        disabled={busy === `start:${link.id}`}
                        onClick={() => toggleAllowStart(link)}
                        type="button"
                      >
                        {link.allowStart ? 'Stop visitors starting' : 'Let visitors start'}
                      </button>
                    )}
                    {canManageLinks && (
                      <button
                        aria-label={`Revoke link created ${created}`}
                        className="share-btn share-btn-quiet share-btn-danger"
                        disabled={busy === `revoke:${link.id}`}
                        onClick={() => revokeLink(link.id)}
                        type="button"
                      >
                        {busy === `revoke:${link.id}` ? 'Revoking…' : 'Revoke'}
                      </button>
                    )}
                    {shownLink?.id === link.id && (
                      <input
                        aria-label="Preview link"
                        autoFocus
                        className="share-link-input share-link-shown"
                        onFocus={(event) => event.target.select()}
                        readOnly
                        type="text"
                        value={shownLink.url}
                      />
                    )}
                    {isEditing && (
                      <div className="share-pw-box share-pw-edit">
                        <label htmlFor={editPasswordId}>{editing.hadPassword ? 'New password for this link' : 'Password for this link'}</label>
                        <div className="share-pw-row">
                          <input
                            aria-describedby={editing.error ? editErrorId : undefined}
                            aria-invalid={editing.error ? 'true' : 'false'}
                            autoComplete="off"
                            className="share-link-input share-pw-input"
                            id={editPasswordId}
                            onChange={(event) => setEditing({ ...editing, value: event.target.value, error: '', saved: false, copied: false })}
                            readOnly={editing.saved}
                            ref={editPasswordRef}
                            spellCheck={false}
                            type="text"
                            value={editing.value}
                          />
                          {!editing.saved && (
                            <button className="share-btn" onClick={() => setEditing({ ...editing, value: generateSharePassword(), error: '', copied: false })} type="button">Generate</button>
                          )}
                          <button aria-label="Copy password" className="share-btn" onClick={copyEditedPassword} type="button">{editing.copied ? 'Copied' : 'Copy'}</button>
                        </div>
                        {editing.error && <p className="share-field-error" id={editErrorId} role="alert">{editing.error}</p>}
                        <p className="share-note">
                          {editing.saved
                            ? 'Saved. Copy it now: Flow keeps only a hash and you can’t see it again later.'
                            : `${editing.hadPassword ? 'Everyone who opened the link with the old password is signed out. ' : ''}${PASSWORD_HINT}`}
                        </p>
                        <div className="share-pw-actions">
                          {editing.saved ? (
                            <button className="share-btn share-btn-primary" onClick={() => setEditing(null)} type="button">Done</button>
                          ) : (
                            <>
                              <button className="share-btn share-btn-primary" disabled={busy === `password:${link.id}`} onClick={() => savePassword(false)} type="button">
                                {busy === `password:${link.id}` ? 'Saving…' : 'Save password'}
                              </button>
                              {editing.hadPassword && (
                                <button className="share-btn" disabled={busy === `password:${link.id}`} onClick={() => savePassword(true)} type="button">Remove password</button>
                              )}
                              <button className="share-btn" onClick={() => setEditing(null)} type="button">Cancel</button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="share-note">{canManageLinks ? 'No active links.' : 'Only editors and the owner can create preview links.'}</p>
          )}
        </section>
      </>
    );
  }

  return createPortal(
    <div className="share-scrim" onClick={onClose} role="presentation">
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className="share-dialog"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="share-head">
          <h2 id={titleId}>Share “{projectName}”</h2>
          <button aria-label="Close" className="share-x" onClick={onClose} type="button">&times;</button>
        </div>
        {body}
        <div aria-live="polite" className="share-sr-only" role="status">{announcement}</div>
      </div>
    </div>,
    document.body
  );
}

ShareDialog.propTypes = {
  // 'people': opened on the people with access and the invite field.
  focus: PropTypes.oneOf(['people']),
  onAccessChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  projectName: PropTypes.string,
  serviceName: PropTypes.string,
};

ShareDialog.defaultProps = { projectName: 'Project' };

/** Shown in the project view when the caller can only look (role viewer). */
export function ReadOnlyNotice({ ownerName }) {
  return (
    <div className="share-readonly-notice" role="note">
      <strong>View only.</strong>{' '}
      {ownerName ? `${ownerName} shared this project with you as a viewer` : 'This project is shared with you as a viewer'}
      {': you can look at the code, the preview and the chats, but not change them.'}
    </div>
  );
}

ReadOnlyNotice.propTypes = { ownerName: PropTypes.string };
